import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

import {
  DEFAULT_STROKE_WIDTH,
  DEFAULT_LEGEND_HEIGHT_OFFSET,
  DEFAULT_DOT_RADIUS,
  GRID_OPACITY,
  CURSOR_OPACITY,
  CURSOR_STROKE_WIDTH,
  TICK_FONT_SIZE,
  TICK_MARGIN,
  Y_AXIS_WIDTH,
  CHART_MARGIN,
  NO_DATA_TYPOGRAPHY_MARGIN,
} from './metric-line-chart.constants';

import {
  tickMoneyFormatter,
  tickNumberFormatter,
  getTicksSettings,
  defaultLegendFormatter,
  moneyFormatter,
  numberFormatter,
} from '../metric-chart.utils';
import {
  DEFAULT_LINE_COLOR,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_TICKS_COUNT,
  NO_DATA_LABEL,
  MAX_COMFORTABLE_TICKS_COUNT,
} from '../metric-chart.constants';
import { MetricChartTicks, MetricTimePoint } from '../metric-chart.types';
import { MetricChartLegend } from '../metric-chart-legend.component';
import { MetricChartTooltip, MetricChartTooltipProps } from '../metric-chart-tooltip.component';

export type MetricLineChartProps = {
  metrics: MetricTimePoint[];
  metricsType?: 'number' | 'currency';
  noDataLabel?: string;
  height?: number;
  lineColor?: string | string[];
  strokeWidth?: number;
  minTicksCount?: MetricChartTicks;
  maxTicksCount?: number;
  dateTickFormatter?: (time: number) => string;
  isLegendVisible?: boolean;
  legendFormatter?: (value: string) => string;
  showDots?: boolean;
  tooltipContent?: MetricChartTooltipProps['tooltipContent'] | null;
  centsThreshold?: number;
};

export const MetricLineChart = ({
  metrics,
  metricsType = 'number',
  height = DEFAULT_CHART_HEIGHT,
  noDataLabel = NO_DATA_LABEL,
  lineColor = DEFAULT_LINE_COLOR,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  minTicksCount = DEFAULT_TICKS_COUNT,
  maxTicksCount = MAX_COMFORTABLE_TICKS_COUNT,
  dateTickFormatter,
  isLegendVisible,
  legendFormatter = defaultLegendFormatter,
  showDots = true,
  tooltipContent,
  centsThreshold,
}: MetricLineChartProps) => {
  const theme = useTheme();

  const data = useMemo(
    () => metrics.map((metric) => ({ date: metric.date.getTime(), value: metric.value })),
    [metrics],
  );

  const gridColor = useMemo(
    () => alpha(theme.palette.text.primary, GRID_OPACITY),
    [theme.palette.text.primary],
  );

  const tickStyle = useMemo(
    () => ({ fontSize: TICK_FONT_SIZE, fill: theme.palette.text.secondary }),
    [theme.palette.text.secondary],
  );

  const { ticks, ticksFormatter, ticksShift, keys } = useMemo(
    () =>
      getTicksSettings(data, {
        minTicksCount,
        maxTicksCount,
        dateTickFormatter,
      }),
    [data, minTicksCount, maxTicksCount, dateTickFormatter],
  );

  const boxHeight = useMemo(
    () => height + (isLegendVisible && metrics.length ? DEFAULT_LEGEND_HEIGHT_OFFSET : 0),
    [height, isLegendVisible, metrics.length],
  );

  const yAxisTickFormatter = useMemo(
    () => (metricsType === 'currency' ? tickMoneyFormatter : tickNumberFormatter),
    [metricsType],
  );

  const cursorStyle = useMemo(
    () => ({
      stroke: alpha(theme.palette.primary.main, CURSOR_OPACITY),
      strokeWidth: CURSOR_STROKE_WIDTH,
    }),
    [theme.palette.primary.main],
  );

  const xAxisDomain: AxisDomain = useMemo(() => {
    return [(min: number) => min - ticksShift, (max: number) => max + ticksShift];
  }, [ticksShift]);

  const currencyFormatter = useMemo(
    () => (value: number) => moneyFormatter(value, centsThreshold),
    [centsThreshold],
  );

  const lineComponents = useMemo(
    () =>
      keys.map((key, index) => {
        const color = Array.isArray(lineColor) ? lineColor[index] : lineColor;

        return (
          <Line
            key={key}
            dataKey={key}
            stroke={color}
            strokeWidth={strokeWidth}
            dot={showDots ? { r: DEFAULT_DOT_RADIUS, fill: color } : false}
            isAnimationActive={false}
            connectNulls={false}
          />
        );
      }),
    [keys, lineColor, strokeWidth, showDots],
  );

  return (
    <Box
      alignContent='center'
      sx={{
        userSelect: 'none',
        width: '100%',
        height: boxHeight,
      }}
    >
      {!metrics.length ? (
        <Typography my={NO_DATA_TYPOGRAPHY_MARGIN} textAlign='center' color='text.secondary'>
          {noDataLabel}
        </Typography>
      ) : (
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid stroke={gridColor} vertical={false} />
            <XAxis
              dataKey='date'
              type='number'
              scale='time'
              domain={xAxisDomain}
              ticks={ticks}
              tickFormatter={ticksFormatter}
              tick={tickStyle}
              tickMargin={TICK_MARGIN}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              tick={tickStyle}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={Y_AXIS_WIDTH}
            />
            {tooltipContent !== null && (
              <Tooltip
                position={{ x: 0, y: 0 }}
                cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                wrapperStyle={{ pointerEvents: 'none' }}
                content={
                  <MetricChartTooltip
                    tooltipContent={tooltipContent}
                    legendFormatter={legendFormatter}
                    ticksFormatter={ticksFormatter}
                    valueFormatter={
                      metricsType === 'currency' ? currencyFormatter : numberFormatter
                    }
                  />
                }
              />
            )}
            {isLegendVisible && (
              <Legend content={<MetricChartLegend />} formatter={legendFormatter} />
            )}
            {lineComponents}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
